import { useState, useEffect, useRef } from "react";

const NetworkMonitor = () => {
    const [speed, setSpeed] = useState(null);
    const [latency, setLatency] = useState(null);
    const [isSlow, setIsSlow] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [isToastVisible, setIsToastVisible] = useState(false);
    
    // Use ref to track last toast time
    const lastToastTime = useRef(0);
    const TOAST_COOLDOWN = 3 * 60 * 1000; // 3 minutes in milliseconds

    useEffect(() => {
        const checkSpeed = async () => {
            if ("connection" in navigator) {
                const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
                setSpeed(connection.downlink);
                const slowConnection = connection.downlink < 6;
                setIsSlow(slowConnection);
                
                // Show toast only if connection is slow and cooldown period has passed
                if (slowConnection && shouldShowToast()) {
                    showSlowNetworkToast();
                }
            }
        };

        const checkLatency = async () => {
            try {
                const start = performance.now();
                await fetch("https://www.google.com", { method: "HEAD", mode: "no-cors" });
                const end = performance.now();

                const ping = (end - start).toFixed(2);
                setLatency(ping);
                
                const highLatency = ping > 500;
                setIsSlow(prev => prev || highLatency);
                
                // Show toast only if latency is high and cooldown period has passed
                if (highLatency && shouldShowToast()) {
                    showSlowNetworkToast();
                }
            } catch (error) {
                // If fetch fails, consider it as slow network
                setIsSlow(true);
                if (shouldShowToast()) {
                    showSlowNetworkToast();
                }
            }
        };

        const shouldShowToast = () => {
            const now = Date.now();
            return !isToastVisible && (now - lastToastTime.current >= TOAST_COOLDOWN);
        };

        const showSlowNetworkToast = () => {
            if (isToastVisible) return; // Prevent multiple toasts
            
            const now = Date.now();
            lastToastTime.current = now; // Update last toast time
            
            setIsToastVisible(true);
            setShowToast(true);
            
            // Auto hide toast after 5 seconds
            setTimeout(() => {
                setShowToast(false);
                // Reset visibility flag after animation completes
                setTimeout(() => {
                    setIsToastVisible(false);
                }, 300);
            }, 5000);
        };

        checkSpeed();
        checkLatency();
        
        const interval = setInterval(() => {
            checkSpeed();
            checkLatency();
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, [isToastVisible]);

    const closeToast = () => {
        setShowToast(false);
        setTimeout(() => {
            setIsToastVisible(false);
        }, 300);
    };

    if (!showToast || !isSlow) return null;

    return (
        <div className={`fixed top-16 right-4 z-50 transform transition-all duration-300 ease-in-out ${
            showToast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}>
            <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 min-w-80 max-w-md">
                <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            <svg 
                                className="w-6 h-6 text-red-100" 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                            >
                                <path 
                                    fillRule="evenodd" 
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                                    clipRule="evenodd" 
                                />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <div className="font-semibold text-sm">
                                Slow Network Detected
                            </div>
                            <div className="text-xs text-red-100 mt-1">
                                {speed && latency ? (
                                    <>Connection: {speed} Mbps • Latency: {latency}ms</>
                                ) : speed ? (
                                    <>Speed: {speed} Mbps</>
                                ) : latency ? (
                                    <>Latency: {latency}ms</>
                                ) : (
                                    <>Poor connection detected</>
                                )}
                            </div>
                            <div className="text-xs text-red-100 mt-1">
                                Some features may load slowly
                            </div>
                        </div>
                    </div>
                    
                    <button
                        onClick={closeToast}
                        className="flex-shrink-0 ml-4 text-red-100 hover:text-white transition-colors"
                        aria-label="Close notification"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path 
                                fillRule="evenodd" 
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                                clipRule="evenodd" 
                            />
                        </svg>
                    </button>
                </div>
                
                {/* Progress bar for auto-hide */}
                <div className="mt-3">
                    <div className="bg-red-400 rounded-full h-1">
                        <div 
                            className="bg-red-200 h-1 rounded-full animate-pulse"
                            style={{
                                animation: 'progress 5s linear forwards'
                            }}
                        ></div>
                    </div>
                </div>
            </div>
            
            <style jsx>{`
                @keyframes progress {
                    from {
                        width: 100%;
                    }
                    to {
                        width: 0%;
                    }
                }
            `}</style>
        </div>
    );
};

export default NetworkMonitor;